'use client';

import type {
  Behavior,
  FieldContent,
  WebhookFieldAction,
  WebhookRequestRow,
  WebhookResponseRow,
} from '@capture/shared';
import { isForbiddenWebhookUrl } from '@capture/shared';
import { WebhookConnectionFields, type WebhookConnectionValue } from '../WebhookConnectionFields';
import { WebhookRequestTable } from '../WebhookRequestTable';
import { WebhookResponseTable } from '../WebhookResponseTable';
import { isDerivedTargetName } from '../DerivedFieldSelect';
import './WebhookBody.css';

type WebhookBehavior = Extract<Behavior, { kind: 'webhook' }>;

interface Props {
  behavior: WebhookBehavior;
  onChange: (b: Behavior) => void;
  fieldNames: string[];
  /** Full field definitions for this job type — threaded into
   *  `WebhookResponseTable` / `DerivedFieldSelect`, which filter out
   *  operator-keyed fields themselves via the shared `isOperatorKeyedField`
   *  predicate. Webhook response-mapping targets must be derived/non-keyed. */
  fields: FieldContent[];
  advanced: boolean;
}

/**
 * Body for the webhook DERIVATION behavior (transform-webhook — distinct from
 * the WU-3.2 webhook_validate RULE). Composes an HTTP request from mapped
 * field values, calls a tenant's external service, and maps the JSON
 * response into derived fields.
 *
 * T5 delivered the connection sub-form + request-builder table. T6 (this
 * task) adds the response-mapping table, on-failure controls, and full
 * config assembly + a client-side mirror of the T2/T4 publish gate so an
 * author sees immediately why a config can't publish yet.
 */
export function WebhookBody({ behavior, onChange, fieldNames, fields }: Props) {
  function handleConnectionChange(next: WebhookConnectionValue) {
    const rest: Record<string, unknown> = { ...behavior };
    delete rest.auth;
    const merged: Record<string, unknown> = { ...rest, url: next.url, timeoutMs: next.timeoutMs };
    if (next.auth !== undefined) merged.auth = next.auth;
    onChange(merged as Behavior);
  }

  function handleRequestChange(rows: WebhookRequestRow[]) {
    onChange({ ...behavior, requestBody: rows });
  }

  function handleResponseChange(rows: WebhookResponseRow[]) {
    onChange({ ...behavior, responseMappings: rows });
  }

  function handleFailModeChange(failMode: 'soft' | 'hard') {
    if (failMode === 'hard') {
      // `onFailure` only governs the soft path (what to do with derived
      // targets on a non-blocking failure); on hard it's never read at
      // runtime. Drop the vestigial key so persisted JSON isn't confusing.
      const { onFailure: _onFailure, ...rest } = behavior;
      onChange({ ...rest, failMode });
      return;
    }
    onChange({ ...behavior, failMode });
  }

  function handleOnFailureActionChange(action: WebhookFieldAction['action']) {
    if (action === 'default') {
      onChange({ ...behavior, onFailure: { action, defaultValue: behavior.onFailure?.defaultValue ?? '' } });
    } else {
      onChange({ ...behavior, onFailure: { action } });
    }
  }

  function handleOnFailureDefaultValueChange(defaultValue: string) {
    onChange({ ...behavior, onFailure: { action: 'default', defaultValue } });
  }

  const failMode = behavior.failMode ?? 'soft';
  const onFailureAction = behavior.onFailure?.action ?? 'leave';

  // Client-side mirror of the transform-webhook T2/T4 publish gate
  // (`validateWebhookTransformConfig` — webhook-transform.ts): the SAME three
  // blocking checks, evaluated here so the author sees the problem
  // immediately instead of only at publish time.
  const invalidReasons: string[] = [];
  const urlProblem = isForbiddenWebhookUrl(behavior.url);
  if (urlProblem) invalidReasons.push(urlProblem);
  if (behavior.requestBody.length === 0) {
    invalidReasons.push('Request body must have at least one row.');
  }
  if (behavior.responseMappings.length === 0) {
    invalidReasons.push('Response mapping must have at least one row.');
  }
  // Close the gap the DerivedFieldSelect stale-target fallback opens: a row
  // whose target is set but is NOT a current derived field (deleted, renamed,
  // or now operator-keyed) would otherwise pass silently client-side and only
  // 409 at publish. Same derived-set source of truth as the per-row flag.
  for (const row of behavior.responseMappings) {
    if (row.target !== '' && !isDerivedTargetName(fields, row.target)) {
      invalidReasons.push(
        `Response mapping targets a field that isn't a derived field: ${row.target}.`,
      );
    }
  }

  return (
    <div className="webhook-body" data-testid="webhook-behavior-body">
      <WebhookConnectionFields
        value={{ url: behavior.url, auth: behavior.auth, timeoutMs: behavior.timeoutMs }}
        onChange={handleConnectionChange}
      />
      <WebhookRequestTable
        rows={behavior.requestBody}
        fieldNames={fieldNames}
        onChange={handleRequestChange}
      />
      <WebhookResponseTable
        rows={behavior.responseMappings}
        fields={fields}
        onChange={handleResponseChange}
      />

      <div className="webhook-body__fail-mode">
        <span className="webhook-body__label">Fail mode</span>
        <label>
          <input
            type="radio"
            name="webhook-fail-mode"
            checked={failMode === 'soft'}
            onChange={() => handleFailModeChange('soft')}
            data-testid="webhook-fail-mode-soft"
          />
          Soft — never blocks the batch on a transport failure
        </label>
        <label>
          <input
            type="radio"
            name="webhook-fail-mode"
            checked={failMode === 'hard'}
            onChange={() => handleFailModeChange('hard')}
            data-testid="webhook-fail-mode-hard"
          />
          Hard — blocks the batch on a transport failure
        </label>
      </div>

      {failMode === 'soft' && (
        <div className="webhook-body__on-failure">
          <label className="webhook-body__label">
            On failure, derived targets
            <select
              value={onFailureAction}
              onChange={(e) => handleOnFailureActionChange(e.target.value as WebhookFieldAction['action'])}
              data-testid="webhook-onfailure-action"
            >
              <option value="leave">Leave as-is</option>
              <option value="clear">Clear</option>
              <option value="default">Set to default</option>
            </select>
          </label>
          {onFailureAction === 'default' && (
            <input
              value={behavior.onFailure?.defaultValue ?? ''}
              onChange={(e) => handleOnFailureDefaultValueChange(e.target.value)}
              placeholder="Default value"
              aria-label="On-failure default value"
              data-testid="webhook-onfailure-default"
            />
          )}
        </div>
      )}

      {invalidReasons.length > 0 && (
        <div className="webhook-body__invalid" role="alert" data-testid="webhook-invalid-summary">
          <strong>This webhook can&rsquo;t be published yet:</strong>
          <ul>
            {invalidReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
